import { useQueryClient } from "@tanstack/react-query";
import { BorrowingBaseSummaryTableBody } from "features/credit-facility/borrowing-base/BorrowingBaseSummaryTableBody";
import { CreditFacilitySetupFooter } from "features/credit-facility/setup/CreditFacilitySetupFooter";
import { CreditFacilitySetupPageProps } from "features/credit-facility/setup/CreditFacilitySetupPageProps";
import {
  getCreditFacilityRetrieveQueryKey,
  useCreditFacilityActivate,
} from "gen/endpoints/credit-facility/credit-facility";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { rootRoute } from "shared/navigation/Routes";

export const CreditFacilitySetupBorrowingBaseSummary: React.FC = () => {
  const { t } = useTranslation(["creditFacility"]);
  const { fundFamilyId, creditFacility } =
    useOutletContext<CreditFacilitySetupPageProps>();
  const navigate = useNavigate();

  const backToSummary = useCallback(() => {
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .creditFacilities({})
        .creditFacility({ creditFacilityId: creditFacility.id }).$,
    );
  }, [navigate, fundFamilyId, creditFacility.id]);

  const { mutateAsync: activate } = useCreditFacilityActivate();
  const queryClient = useQueryClient();
  return (
    <PageContent>
      <PageHeader
        workflowName={t("creditFacility:setup.title")}
        title={t("creditFacility:setup.summary.title")}
      />
      <PageBody>
        <TableBox isFullHeight={true}>
          <TableBoxHeader>
            <TableBoxTitle>
              {t("creditFacility:borrowingBase.advanceRateGroups")}
            </TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <BorrowingBaseSummaryTableBody
            creditFacilityId={creditFacility.id}
            effectiveDate={creditFacility.inception_date}
          />
        </TableBox>
        <CreditFacilitySetupFooter
          onClickContinue={async () => {
            await activate({
              fundFamilyId,
              creditFacilityId: creditFacility.id,
            });
            await queryClient.invalidateQueries({
              queryKey: getCreditFacilityRetrieveQueryKey(
                fundFamilyId,
                creditFacility.id,
              ),
            });
            backToSummary();
          }}
          submitText={t("creditFacility:setup.finalizeCreditFacility")}
        />
      </PageBody>
    </PageContent>
  );
};
